import { Alert, AlertDescription, AlertTitle } from "@components/ui/alert";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  CustomPageFormData,
  CustomPageSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { CustomPageData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import { Terminal } from "lucide-react";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

const CustomPageForm: React.FC = () => {
  const queryClient = useQueryClient();
  const { handleAction, searchParams } = useHandleAction({});
  const pageItem = searchParams.get("page-item");
  // const pageMode = searchParams.get("page");
  const pathname = usePathname();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CustomPageFormData>({
    mode: "onChange",
    resolver: zodResolver(CustomPageSchema),
  });

  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<CustomPageData>
  >({
    endpoint: endpoint["custompage"],
    id: pageItem || "",
    key: "custompage" + pageItem,
    enabled: pageItem ? true : false,
  });
  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();

  const { mutate } = usePostData();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);

    if (searchParams.get("page") == "add") {
      return reset({});
    }

    if (data && searchParams.get("page") !== "add") {
      return reset({
        title: data?.data?.title,
        html_content: data?.data?.html_content,
        url: data?.data?.url,
        header_code: data?.data?.header_code,
        footer_code: data?.data?.footer_code,
      });
    }
  }, [data, reset, setIsRetrive]);

  const handleMutate = (data: any) => {
    setIsFormPending(true);
    if (pageItem) {
      data = {
        ...data,
        id: pageItem,
      };
    }

    return {
      data,
      endpoint: endpoint["custompage"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      showToast({
        description: `Custom page ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
      handleAction({ type: "page-close" });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }

    setIsFormPending(false);
    throwErrorAlert(error);
  };

  const onSubmit: SubmitHandler<CustomPageFormData> = (data) => {
    console.log("data", data);
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "title",
      label: "Title",
      colSpan: 6,
      type: "text",
      placeholder: "Enter Student",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },

    {
      name: "url",
      label: "Url",
      colSpan: 6,
      type: "text",
      placeholder: "Enter Url",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "component",
      colSpan: 12,
      component: (
        <>
          <Alert variant="default">
            <Terminal />
            <AlertTitle>
              Heads up!{" "}
              <strong className="text-red-600">Only Use Inline CSS</strong> to
              create Custom pages
            </AlertTitle>
          </Alert>
        </>
      ),
    },
    {
      name: "html_content",
      label: "HTML Editor",
      colSpan: 12,
      type: "code-mirror",
      placeholder: "Enter Certificate Template",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "header_code",
      label: "Header Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `min-w-full  w-full`,
      visable: true,
    },
    {
      name: "footer_code",
      label: "Footer Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
  ];
  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );
  return (
    <>
      <form
        id={pathname}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full mt-4"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px] mt-4`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default CustomPageForm;
